import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Utensils, Plus, MoreVertical, Edit, Trash2, Loader2, Flame } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMeals } from '@/hooks/use-meals';
import { AddMealDialog } from './AddMealDialog';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Meal } from '@/lib/firebase/queries/meals';

interface RecordingMealsWidgetProps {
  organizationId?: string;
}

export function RecordingMealsWidget({ organizationId }: RecordingMealsWidgetProps) {
  const { t, dir } = useLanguage();
  const { meals, loading, addMeal, editMeal, removeMeal } = useMeals();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);

  const handleDialogSuccess = async (name: string, time: string, notes?: string, calories?: number) => {
    const mealTime = new Date(time);
    
    if (editingMeal) {
      await editMeal(editingMeal.id, { name, time: mealTime, notes, calories });
      setEditingMeal(null);
    } else {
      await addMeal({ name, time: mealTime, notes, calories });
      setShowAddDialog(false);
    }
  };

  const handleDeleteMeal = async (mealId: string) => {
    if (confirm(t('meals.confirmDelete') || 'Are you sure you want to delete this meal?')) {
      await removeMeal(mealId);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Utensils className="h-5 w-5 text-primary" />
            {t('dashboard.customer.dashboard.recordingMeals.title')}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="bg-white hover:bg-gray-100"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('dashboard.customer.dashboard.recordingMeals.addMeal')}</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : meals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('meals.noMeals') || 'No meals logged today'}
          </div>
        ) : (
          <div className="space-y-3">
            {meals.map((meal, index) => (
              <motion.div
                key={meal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-purple-500/15 flex items-center justify-center">
                  <Utensils className="h-5 w-5 text-purple-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{meal.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="text-sm text-muted-foreground">
                      {format(meal.time, 'p')}
                    </div>
                    {meal.calories !== undefined && meal.calories !== null && (
                      <Badge variant="outline" className="text-xs bg-red-500/10 text-red-500 border-red-500/20">
                        <Plus className="h-3 w-3 mr-1" />
                        {meal.calories} kcal
                      </Badge>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'}>
                    <DropdownMenuItem onClick={() => setEditingMeal(meal)}>
                      <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('common.edit') || 'Edit'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDeleteMeal(meal.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('common.delete') || 'Delete'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </motion.div>
            ))}
          </div>
        )}
      </CardContent>

      <AddMealDialog
        open={showAddDialog || !!editingMeal}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditingMeal(null);
          }
        }}
        onSuccess={handleDialogSuccess}
        meal={editingMeal}
      />
    </Card>
  );
}

